import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { api } from '@/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Loader2, FileQuestion, ClipboardList, FileDiff, Package, AlertTriangle, Scale,
  FolderKanban, Printer, BarChart3, TrendingUp, ArrowRight, ArrowLeft, RefreshCw,
  Bell, Users, FileImage, FolderCog, ShoppingCart, Calendar, CheckSquare, Download, Shield, Gavel,
  FileBarChart, BookOpen, Wrench
} from 'lucide-react';
import { WalkthroughTrigger } from '@/components/walkthrough';

const METRIC_CONFIG = [
  { key: 'rfis', label: 'RFIs', icon: FileQuestion, openKey: 'open', color: 'blue' },
  { key: 'site_instructions', label: 'Site Instructions', icon: ClipboardList, openKey: 'open', color: 'indigo' },
  { key: 'variation_orders', label: 'Variation Orders', icon: FileDiff, openKey: 'open', color: 'purple' },
  { key: 'delay_events', label: 'Delay Events', icon: AlertTriangle, openKey: 'open', color: 'amber' },
  { key: 'claims', label: 'Claims', icon: Scale, openKey: 'open', color: 'red' },
  { key: 'notices', label: 'Notices', icon: Bell, openKey: 'open', color: 'orange' },
  { key: 'shop_drawings', label: 'Shop Drawings', icon: Package, openKey: 'active', color: 'cyan' },
  { key: 'drawings', label: 'Drawings', icon: FileImage, openKey: 'open', color: 'teal' },
  { key: 'meeting_minutes', label: 'Minutes', icon: Users, openKey: 'pending', color: 'pink' },
  { key: 'programs', label: 'Programmes', icon: FolderCog, openKey: 'active', color: 'slate' },
  { key: 'procurement', label: 'Procurement', icon: ShoppingCart, openKey: 'open', color: 'emerald' },
  { key: 'irs', label: 'IRS', icon: Calendar, openKey: 'open', color: 'violet' },
  { key: 'tasks', label: 'Tasks', icon: CheckSquare, openKey: 'open', color: 'rose' },
  { key: 'disputes', label: 'Disputes', icon: Gavel, openKey: 'active', color: 'red' },
  { key: 'closeout', label: 'Close-Out', icon: Shield, openKey: 'active', color: 'emerald' },
  { key: 'periodic_reports', label: 'Reports', icon: FileBarChart, openKey: 'active', color: 'sky' },
  { key: 'site_diary', label: 'Site Diary', icon: BookOpen, openKey: 'active', color: 'green' },
  { key: 'plant', label: 'Plant', icon: Wrench, openKey: 'active', color: 'stone' },
];

const TABLE_COLUMNS = [
  { key: 'open_rfis', label: 'RFIs', short: 'RFI' },
  { key: 'open_sis', label: 'SIs', short: 'SI' },
  { key: 'open_vos', label: 'VOs', short: 'VO' },
  { key: 'open_delays', label: 'Delays', short: 'Del' },
  { key: 'open_claims', label: 'Claims', short: 'Clm' },
  { key: 'open_notices', label: 'Notices', short: 'Not' },
  { key: 'active_shop_drawings', label: 'Shop Dwg', short: 'SDw' },
  { key: 'open_drawings', label: 'Drawings', short: 'Drw' },
  { key: 'pending_minutes', label: 'Minutes', short: 'Min' },
  { key: 'active_programs', label: 'Prog', short: 'Prg' },
  { key: 'open_procurement', label: 'Procure', short: 'Prc' },
  { key: 'open_irs', label: 'IRS', short: 'IRS' },
  { key: 'open_tasks', label: 'Tasks', short: 'Tsk' },
  { key: 'active_disputes', label: 'Disputes', short: 'Dsp' },
  { key: 'active_closeout', label: 'Close-Out', short: 'Cls' },
  { key: 'active_periodic_reports', label: 'Reports', short: 'Rpt' },
  { key: 'active_site_diary', label: 'Diary', short: 'Dry' },
  { key: 'active_plant', label: 'Plant', short: 'Plt' },
];

const COLOR_MAP = {
  blue:    { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', accent: 'text-blue-900', badge: 'bg-blue-100 text-blue-700' },
  indigo:  { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', accent: 'text-indigo-900', badge: 'bg-indigo-100 text-indigo-700' },
  purple:  { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', accent: 'text-purple-900', badge: 'bg-purple-100 text-purple-700' },
  cyan:    { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', accent: 'text-cyan-900', badge: 'bg-cyan-100 text-cyan-700' },
  amber:   { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', accent: 'text-amber-900', badge: 'bg-amber-100 text-amber-700' },
  red:     { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', accent: 'text-red-900', badge: 'bg-red-100 text-red-700' },
  orange:  { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', accent: 'text-orange-900', badge: 'bg-orange-100 text-orange-700' },
  teal:    { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', accent: 'text-teal-900', badge: 'bg-teal-100 text-teal-700' },
  pink:    { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700', accent: 'text-pink-900', badge: 'bg-pink-100 text-pink-700' },
  slate:   { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', accent: 'text-slate-900', badge: 'bg-slate-100 text-slate-700' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', accent: 'text-emerald-900', badge: 'bg-emerald-100 text-emerald-700' },
  violet:  { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', accent: 'text-violet-900', badge: 'bg-violet-100 text-violet-700' },
  rose:    { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', accent: 'text-rose-900', badge: 'bg-rose-100 text-rose-700' },
  sky:     { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', accent: 'text-sky-900', badge: 'bg-sky-100 text-sky-700' },
  green:   { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', accent: 'text-green-900', badge: 'bg-green-100 text-green-700' },
  stone:   { bg: 'bg-stone-50', border: 'border-stone-200', text: 'text-stone-700', accent: 'text-stone-900', badge: 'bg-stone-100 text-stone-700' },
};

export default function ExecutiveDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const printRef = useRef(null);

  const loadData = useCallback(() => {
    setLoading(true);
    api.get('/reports/executive-dashboard')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handlePrint = () => { window.print(); };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await api.get('/reports/executive-dashboard/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Executive_Dashboard_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.print();
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="text-center py-20 text-slate-500">Failed to load executive dashboard.</div>
      </DashboardLayout>
    );
  }

  const totalOpen = METRIC_CONFIG.reduce((sum, m) => sum + (data.aggregate[m.key]?.[m.openKey] || 0), 0);

  return (
    <DashboardLayout>
    <div className="space-y-4 sm:space-y-6 print:space-y-4" ref={printRef}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 print:block">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/')} className="rounded-sm print:hidden h-9 w-9 p-0 sm:h-auto sm:w-auto sm:p-2" data-testid="exec-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-lg sm:text-2xl font-barlow font-bold uppercase tracking-wider text-slate-900" data-testid="exec-dashboard-title">
              Executive Dashboard
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              {data.total_projects} project{data.total_projects !== 1 ? 's' : ''} &middot; {new Date(data.generated_at).toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={loadData} className="rounded-sm" data-testid="exec-refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <WalkthroughTrigger moduleId="executive_dashboard" />
          <Button variant="outline" size="sm" onClick={handlePrint} className="rounded-sm" data-testid="print-dashboard-btn">
            <Printer className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Print</span>
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPDF} disabled={downloading} className="rounded-sm" data-testid="download-pdf-btn">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 sm:mr-2" />}
            <span className="hidden sm:inline">PDF</span>
          </Button>
        </div>
      </div>

      {/* Summary Banner */}
      <Card className={`${totalOpen > 0 ? 'border-amber-300 bg-amber-50/50' : 'border-green-300 bg-green-50/50'}`} data-testid="open-items-banner">
        <CardContent className="p-3 sm:pt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-sm flex items-center justify-center shrink-0 ${totalOpen > 0 ? 'bg-amber-100' : 'bg-green-100'}`}>
              <TrendingUp className={`h-5 w-5 sm:h-6 sm:w-6 ${totalOpen > 0 ? 'text-amber-600' : 'text-green-600'}`} />
            </div>
            <div>
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500">Open / Active Items</p>
              <p className={`text-2xl sm:text-3xl font-bold ${totalOpen > 0 ? 'text-amber-700' : 'text-green-700'}`}>{totalOpen}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            {METRIC_CONFIG.map(m => {
              const val = data.aggregate[m.key]?.[m.openKey] || 0;
              if (val === 0) return null;
              const c = COLOR_MAP[m.color];
              return (
                <div key={m.key} className="text-center min-w-[40px]">
                  <p className={`text-sm sm:text-lg font-bold ${c.accent}`}>{val}</p>
                  <p className="text-[8px] sm:text-[10px] uppercase tracking-wider text-slate-500">{m.label}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Module Metrics Grid — responsive: 2 cols mobile, 3 tablet, 4-5 desktop */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-2 sm:gap-3" data-testid="metrics-grid">
        {METRIC_CONFIG.map(m => {
          const agg = data.aggregate[m.key] || {};
          const total = agg.total || 0;
          const open = agg[m.openKey] || 0;
          const c = COLOR_MAP[m.color];
          const Icon = m.icon;
          return (
            <Card key={m.key} className={`${c.border} ${open > 0 ? c.bg : ''}`} data-testid={`metric-card-${m.key}`}>
              <CardContent className="p-2.5 sm:p-3">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Icon className={`h-3.5 w-3.5 ${c.text}`} />
                  <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-slate-500 truncate">{m.label}</p>
                </div>
                <p className={`text-xl sm:text-2xl font-bold ${c.accent}`}>{total}</p>
                <div className="flex items-center justify-between mt-1">
                  <Badge className={`rounded-sm text-[9px] sm:text-[10px] px-1.5 ${c.badge}`}>
                    {open} {m.openKey}
                  </Badge>
                  <span className="text-[9px] sm:text-[10px] text-slate-400">{total - open} done</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Per-Project Breakdown Table */}
      <Card className="border-slate-200" data-testid="project-breakdown">
        <CardContent className="p-3 sm:pt-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Project Breakdown
            </h2>
          </div>
          <div className="overflow-x-auto -mx-3 px-3">
            <table className="w-full text-sm min-w-[800px]" data-testid="project-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-slate-400 sticky left-0 bg-white z-10">Project</th>
                  <th className="text-center py-2 px-1.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-amber-500">Open</th>
                  {TABLE_COLUMNS.map(col => (
                    <th key={col.key} className="text-center py-2 px-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      <span className="hidden lg:inline">{col.label}</span>
                      <span className="lg:hidden">{col.short}</span>
                    </th>
                  ))}
                  <th className="py-2 px-1 print:hidden w-8"></th>
                </tr>
              </thead>
              <tbody>
                {data.projects.map((proj) => {
                  const openItems = proj.open_items || 0;
                  return (
                    <tr key={proj.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-2 px-2 sticky left-0 bg-white z-10">
                        <div className="flex items-center gap-1.5">
                          <FolderKanban className="h-3 w-3 text-slate-400 shrink-0 hidden sm:block" />
                          <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-slate-800 truncate max-w-[120px] sm:max-w-[200px]">{proj.name}</p>
                            {proj.client_name && <p className="text-[9px] text-slate-400 truncate max-w-[120px] sm:max-w-[200px] hidden sm:block">{proj.client_name}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="text-center py-2 px-1.5">
                        <span className={`text-xs sm:text-sm font-bold ${openItems > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                          {openItems}
                        </span>
                      </td>
                      {TABLE_COLUMNS.map(col => {
                        const val = proj[col.key] || 0;
                        return (
                          <td key={col.key} className={`text-center py-2 px-1 text-xs ${val > 0 ? 'text-slate-700 font-medium' : 'text-slate-300'}`}>
                            {val || '-'}
                          </td>
                        );
                      })}
                      <td className="py-2 px-1 print:hidden">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/projects/${proj.id}`)} className="h-6 w-6 p-0" data-testid={`view-project-${proj.id}`}>
                          <ArrowRight className="h-3 w-3 text-slate-400" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
}
